import * as SwitchPrimitive from "@radix-ui/react-switch";
import { cn } from "../../lib/cn.js";

/**
 * A two-state control that announces itself.
 *
 * Radix rather than the `aria-pressed` button idiom used by `SeverityFilter`
 * and the factor lab: those are filters, where "pressed" is the honest word,
 * and this turns a strategy off for everybody. `role="switch"` says that, and
 * Space and Enter come with it.
 *
 * `label` is required and lands on `aria-label`, because the visible label
 * sits in the row beside the control rather than wrapping it.
 *
 * Square, like everything else. The round dots in the confluence strip are
 * the only radius in the app, because there the shape carries the meaning;
 * here it would just be decoration. See docs/design.md, "Rules, not boxes".
 */
export function Switch({
  checked,
  onCheckedChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <SwitchPrimitive.Root
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={label}
      className={cn(
        "relative h-6 w-12 shrink-0 border transition-colors",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        checked ? "border-accent bg-accent/40" : "border-border bg-surface-raised",
        disabled && "cursor-not-allowed opacity-50",
      )}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          "block h-4 w-4 transition-transform",
          checked ? "translate-x-7 bg-text" : "translate-x-1 bg-text-muted",
        )}
      />
    </SwitchPrimitive.Root>
  );
}
